import * as THREE from 'three';
import { random } from '../look/noise.js';

/**
 * A school of fish (part 2's sea floor, 29 Sep 2026): some forty small
 * silvery fish of eight triangles each, one instanced mesh, swimming
 * together round a loop off the stage, each a little off the school's line
 * and its tail beating in the vertex shader. `level` (a feature's, 0–1)
 * brings the school in or sends it off, scattering.
 */

/** A fish seen from anywhere: a lozenge body and a forked tail, nose to −z, 30 cm long. */
export function fishGeometry(): THREE.BufferGeometry {
  // prettier-ignore
  const positions = [
    0, 0, -0.16,   0, 0.05, -0.02,   0, -0.04, -0.02,   0.025, 0, -0.03,  -0.025, 0, -0.03, // nose, back, belly, flanks
    0, 0, 0.09,                                                                              // tail root
    0, 0.05, 0.15,  0, -0.05, 0.15,                                                          // tail tips
  ];
  // How far a vertex swings as the tail beats: nothing at the nose, most at the tail.
  const beat = [0, 0.15, 0.15, 0.1, 0.1, 0.6, 1, 1];
  // prettier-ignore
  const index = [
    0, 3, 1,  0, 1, 4,  0, 2, 3,  0, 4, 2,   // head, both sides
    1, 3, 5,  4, 1, 5,  3, 2, 5,  2, 4, 5,   // body to the tail root
    5, 6, 7,                                 // tail
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('beat', new THREE.Float32BufferAttribute(beat, 1));
  geometry.setIndex(index);
  geometry.computeVertexNormals();
  return geometry;
}

export type School = {
  readonly centre: readonly [number, number, number];
  /** The loop's radius, and how far the fish spread about the school's line. */
  readonly radius: number;
  readonly spread: number;
  readonly count: number;
  readonly seed: number;
  /** Metres about the school's height they swim between; −0.6 to 0.6 by default. */
  readonly height?: readonly [number, number];
  /** How fast time goes for them; 1 by default. */
  readonly speed?: number;
  /** How big, as a multiple of the usual; 1 by default. */
  readonly size?: number;
  /** The range their silver runs between; the usual by default. */
  readonly colours?: readonly [string, string];
};

/** Where fish `i` of the school is at `t`: the school goes round its loop; each keeps its place in it. */
export function fishAt(
  school: School,
  offsets: readonly THREE.Vector3[],
  i: number,
  t: number,
  into = new THREE.Vector3(),
): THREE.Vector3 {
  const [low, high] = school.height ?? [-0.6, 0.6];
  t *= school.speed ?? 1;
  const lag = offsets[i]!.z * 0.08;
  const a = t * 0.22 - lag;
  const wobble = Math.sin(t * 0.7 + i) * 0.1;
  return into.set(
    school.centre[0] + Math.cos(a) * (school.radius + offsets[i]!.x + wobble),
    school.centre[1] + offsets[i]!.y + (low + high) / 2 + ((high - low) / 2) * Math.sin(t * 0.31),
    school.centre[2] + Math.sin(a) * (school.radius + offsets[i]!.x) * 0.7,
  );
}

export function buildSchool(
  school: School,
  clock: THREE.IUniform<number>,
): { mesh: THREE.InstancedMesh; update(elapsed: number, level: number): void } {
  const material = new THREE.MeshStandardMaterial({
    roughness: 0.35,
    metalness: 0.6,
    side: THREE.DoubleSide,
  });
  material.onBeforeCompile = (shader) => {
    shader.uniforms['uClock'] = clock;
    shader.vertexShader =
      'attribute float beat;\nuniform float uClock;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        transformed.x += beat * 0.035 * sin(uClock * 11.0 + float(gl_InstanceID) * 1.7);`,
      );
  };
  material.customProgramCacheKey = () => 'fish';
  const mesh = new THREE.InstancedMesh(fishGeometry(), material, school.count);
  mesh.name = 'fish-school';
  mesh.frustumCulled = false;
  const next = random(school.seed);
  const offsets = Array.from(
    { length: school.count },
    () =>
      new THREE.Vector3(
        (next() - 0.5) * school.spread,
        (next() - 0.5) * school.spread * 0.6,
        (next() - 0.5) * school.spread * 2,
      ),
  );
  const colour = new THREE.Color();
  const low = school.colours
    ? new THREE.Color(school.colours[0])
    : new THREE.Color(0.62, 0.72, 0.8);
  const high = school.colours
    ? new THREE.Color(school.colours[1])
    : new THREE.Color(0.82, 0.87, 0.95);
  for (let i = 0; i < school.count; i++) {
    mesh.setColorAt(
      i,
      colour.setRGB(
        low.r + (high.r - low.r) * next(),
        low.g + (high.g - low.g) * next(),
        low.b + (high.b - low.b) * next(),
      ),
    );
  }
  const matrix = new THREE.Matrix4();
  const here = new THREE.Vector3();
  const ahead = new THREE.Vector3();
  const look = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const size = new THREE.Vector3();
  const update = (elapsed: number, level: number): void => {
    mesh.visible = level > 0.01;
    if (!mesh.visible) return;
    for (let i = 0; i < school.count; i++) {
      fishAt(school, offsets, i, elapsed, here);
      fishAt(school, offsets, i, elapsed + 0.2, ahead);
      // Scattering outwards and away as the school leaves.
      const away = (1 - level) * 12;
      here.x += (here.x - school.centre[0]) * away * 0.08;
      here.z += (here.z - school.centre[2]) * away * 0.08;
      here.y += away * 0.3;
      // Nose (−z) along the way it swims.
      look.lookAt(here, ahead, up);
      q.setFromRotationMatrix(look);
      size.setScalar(
        Math.min(1, level * 1.4) * (0.8 + (0.4 * ((i * 7) % 5)) / 5) * (school.size ?? 1),
      );
      mesh.setMatrixAt(i, matrix.compose(here, q, size));
    }
    mesh.instanceMatrix.needsUpdate = true;
  };
  update(0, 1);
  return { mesh, update };
}
